/**
 * Placeholder items on a managed list: "Other" and "N/A".
 *
 * Both are ordinary list rows with no CRM account, recognized by their text.
 * "Other" follows the list's vba_includeotheroption flag and opens a
 * type-a-new-value box in Ops. "N/A" (ADO #1612) has no flag: the item being on
 * the list IS the setting, and picking it just records N/A. Both sort to the
 * bottom, Other then N/A, and Ops dropdowns set them off from the real values
 * with a gap.
 *
 * Pure (no API imports) so the vendor sync plan and tests can use it.
 */

import type { ManagedListItem } from './types';

export const OTHER_ITEM_VALUE = 'Other';
export const NA_ITEM_VALUE = 'N/A';

type ItemLike = Pick<ManagedListItem, 'value' | 'sourceAccountId'>;

const norm = (value: string) => value.trim().toLowerCase();

export function isOtherValue(value: string): boolean {
  return norm(value) === 'other';
}

export function isNaValue(value: string): boolean {
  return norm(value) === 'n/a';
}

export function isPlaceholderValue(value: string): boolean {
  return isOtherValue(value) || isNaValue(value);
}

/** The "Other" item: that text with no CRM account behind it. */
export function isOtherItem(item: ItemLike): boolean {
  return !item.sourceAccountId && isOtherValue(item.value);
}

/** The "N/A" item: that text with no CRM account behind it. */
export function isNaItem(item: ItemLike): boolean {
  return !item.sourceAccountId && isNaValue(item.value);
}

export function isPlaceholderItem(item: ItemLike): boolean {
  return isOtherItem(item) || isNaItem(item);
}

/** Real values 0, Other 1, N/A 2. */
function placeholderRank(item: ItemLike): number {
  if (isOtherItem(item)) return 1;
  if (isNaItem(item)) return 2;
  return 0;
}

/** Default item order: A to Z (case-insensitive), then Other, then N/A. */
export function compareManagedListItems(a: ItemLike, b: ItemLike): number {
  const rank = placeholderRank(a) - placeholderRank(b);
  if (rank !== 0) return rank;
  return a.value.localeCompare(b.value, undefined, { sensitivity: 'base' });
}

/**
 * Split dropdown choices into the real values and the trailing run of
 * placeholders, so the dropdown can put a gap between them. Only a TRAILING run
 * splits off: list items already sort placeholders last, and an inline choice
 * list that holds "Other" mid-list keeps its authored order. A list that is all
 * placeholders has nothing to separate.
 */
export function splitPlaceholderTail(choices: readonly string[]): { values: string[]; tail: string[] } {
  let cut = choices.length;
  while (cut > 0 && isPlaceholderValue(choices[cut - 1]!)) cut--;
  if (cut === 0 || cut === choices.length) return { values: [...choices], tail: [] };
  return { values: choices.slice(0, cut), tail: choices.slice(cut) };
}
